import { djb2Hash } from '@/modules/auth/auth.hash';

/** HTTP methods supported by the API */
export const API_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
} as const;

export type HttpMethod = (typeof API_METHODS)[keyof typeof API_METHODS];

/**
 * Hash body for cache key deduplication.
 * Same body = same hash = same cache entry.
 */
export const hashBody = (body: unknown): string | null => {
  if (body === undefined || body === null) return null;
  return djb2Hash(JSON.stringify(body));
};

export type CacheKeyParams = {
  endpoint: string;
  method?: HttpMethod;
  tags?: string[];
  query?: object;
  body?: unknown;
};

/**
 * Builds SWR cache key array with fixed positions.
 * Structure: ['api', method, endpoint, tags, query, bodyHash]
 * Positions are fixed (nulls preserved) for reliable tag matching.
 */
export const buildCacheKey = ({
  endpoint,
  method = 'GET',
  tags,
  query,
  body,
}: CacheKeyParams): [
  string,
  string,
  string,
  string[] | null,
  object | null,
  string | null,
] => [
  'api',
  method,
  endpoint,
  tags?.length ? tags : null,
  query ?? null,
  hashBody(body),
];

/**
 * True when `key` is the cache key of a plain (query-less) GET read of
 * `endpoint`. Query'd variants are deliberately excluded: they may hold
 * filtered/paginated subsets that a full mutation response must not
 * overwrite — they stay on the tag-revalidation path instead. Lives here so
 * the positional key layout has a single owner.
 */
export const isPlainEndpointGetKey = (
  key: unknown[],
  endpoint: string,
): boolean =>
  key[0] === 'api' &&
  key[1] === 'GET' &&
  key[2] === endpoint &&
  (key[4] === null || key[4] === undefined);
